import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import { MainTableFilterModel } from 'src/app/shared/models/main-table-filter.model';
import { environment } from 'src/environments/environment';
import { IdName } from '../../shared/models/id-name';
import { IDepartment, IDepartmentUser, IDepartmentWithGroups } from '../models/department.model';
import { DepartmentService } from './department.service';

@Injectable({
  providedIn: 'root',
})
export class DepartmentApiService {
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
    params: new HttpParams(),
  };

  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/departments`;
  }

  private urlForSelectedTenant(tenantId: string): string {
    return `${environment.apiUrl}/api/${tenantId}/departments`;
  }

  private get selectedDepartment(): string {
    return getDepartmentId();
  }

  constructor(private http: HttpClient) {}

  getDepartments(currentFilter?: MainTableFilterModel): Observable<ApiResponse<IDepartment[]>> {
    let params = new HttpParams();

    if (currentFilter) {
      params = params.set('Page', currentFilter.pageIndex).set('PageSize', currentFilter.pageSize);

      if (currentFilter.searchTerm) {
        params = params.set('GlobalSearch', currentFilter.searchTerm);
      }

      if (currentFilter.orderBy && currentFilter.ascDesc) {
        params = params.set(`OrderBy[${currentFilter.orderBy}]`, currentFilter.ascDesc);
      }
    }

    return this.http.get<ApiResponse<IDepartment[]>>(this.url, { ...this.httpOptions, params });
  }

  getAllDepartments(): Observable<IdName[]> {
    const url = `${this.url}/List`;
    return this.http.get<IdName[]>(url, this.httpOptions);
  }

  getAccessedByEmployment(): Observable<IdName[]> {
    const url = `${this.url}/accessedByEmployment`;
    return this.http.get<IdName[]>(url, this.httpOptions);
  }

  getAllDepartmentsForSelectedTenant(tenantId: string): Observable<IdName[]> {
    const url = `${this.urlForSelectedTenant(tenantId || getTenantId())}/List`;
    return this.http.get<IdName[]>(url, this.httpOptions);
  }

  getAllDepartmentsForMenuId(menuId: string): Observable<IdName[]> {
    const url = `${environment.apiUrl}/api/${getTenantId()}/menu/${menuId}/departments`;
    return this.http.get<IdName[]>(url, this.httpOptions);
  }

  getDepartmentsForUser(): Observable<IDepartmentWithGroups[]> {
    const url = `${this.url}/forUser`;
    return this.http.get<IDepartmentWithGroups[]>(url, this.httpOptions);
  }

  getSelectedDepartmentUsers(config: {
    includeDeleted?: boolean;
    onlyEmployed?: boolean;
  }): Observable<IDepartmentUser[]> {
    const { includeDeleted, onlyEmployed } = config;
    const url = `${this.url}/${
      this.selectedDepartment
    }/users?IncludeDeleted=${!!includeDeleted}&OnlyEmployed=${!!onlyEmployed}`;
    return this.http.get<IDepartmentUser[]>(url, this.httpOptions);
  }

  getDepartmentById(departmentId: string) {
    const url = `${this.url}/${departmentId}`;
    return this.http.get<IDepartment>(url);
  }

  createDepartment(department: IDepartment): Observable<Response> {
    const formData = DepartmentService.convertToFormData(department, getTenantId());
    return this.http.post<Response>(this.url, formData);
  }

  updateDepartment(department: IDepartment): Observable<IDepartment> {
    const formData = DepartmentService.convertToFormData(department, getTenantId());
    return this.http.put<IDepartment>(this.url, formData);
  }

  deleteDepartment(departmentId: string): Observable<IDepartment> {
    const url = `${this.url}/${departmentId}`;
    return this.http.delete<IDepartment>(url, this.httpOptions);
  }
}
